// ENH-344: which surfaces a shopper's own answers are captured from.
//
// Product rule, in full:
//   1. Advocate, logged OUT, Email Gate widget ....... captured
//   2. Friend,   logged OUT, Friend widget ........... captured
//   3. Friend,   logged IN,  Friend widget ........... captured
//   4. Advocate, logged IN,  Email Share widget ...... NOT captured
//
// Only case 4 is excluded. A logged-in advocate never sees an email gate — the
// widget opens straight on the share screen — and answers are captured from the
// gate only.
//
// Governs BOTH the merchant's custom fields AND the two known fields (birthday,
// zipCode), which is why this is named for the answers rather than for custom
// attributes. Dropping birthday here costs nothing downstream: Samantha
// confirmed (2026-08-12) that a birthday given on a referral widget does not
// earn the Birthday Reward — that fires from the loyalty widget only.

/**
 * The actions on which an IDENTIFIED shopper submits answers about themselves,
 * rather than sharing with someone else.
 *
 * An allowlist, not a denylist of advocate actions. The first attempt listed
 * `getReferral`, `emailShare`, `getShare`, `productShare` and excluded those —
 * which missed every real share channel. A share is created through
 * `facebookShare`, `twitterShare`, `smsShare`, `whatsAppShare`, `mailtoShare`,
 * `messengerShare`, `linkedInShare`, `instagramShare` and `nativeMobileShare`.
 * Most of those — every one `shouldPreload()` lists, so all but
 * `instagramShare` — ALSO have a `…Preload` variant that `componentDidMount`
 * fires on its own (`preload()` calls `runAction(action.name + "Preload")`).
 * None of those names were on the list, so case 4 below was not actually
 * implemented for a normal Email Share widget: for the preloading channels a
 * logged-in advocate's answers rode along on the very first render, before they
 * touched anything.
 *
 * Enumerating the channels instead would work today and rot tomorrow — the next
 * channel added to `runAction` reopens the hole silently, and nothing fails.
 * Inverting it fails the safe way: an unrecognised action on an identified
 * shopper does not carry their answers, and adding a channel changes nothing.
 *
 * `trackEvent` is the friend's email capture — the one that must keep working.
 * `loyaltyOptIn` is the shopper acting on their own loyalty record, so their
 * answers are theirs; listed to preserve existing behaviour rather than as a
 * new decision.
 */
export const SHOPPER_SUBMISSION_ACTIONS = ["trackEvent", "loyaltyOptIn"];

/**
 * Whether a widget action should carry the shopper's own answers — the
 * merchant's custom fields, and the birthday / zipCode known fields.
 *
 * `hasCustomerId` is `data.customer.id` — how the widget already recognises a
 * skipped email gate. `withActions` keys its consent stripping off the same
 * value for the same reason: a shopper the merchant identified never saw the
 * gate, so never saw the opt-in checkbox either.
 *
 * Logged out, everything is captured: a logged-out advocate types into the gate
 * (case 1) and a logged-out friend into the friend widget (case 2), and case 1's
 * answers can be typed on the share screen AFTER `getReferral` has gone out, so
 * the action name cannot be used to exclude anything there.
 *
 * Identified, only the shopper's own submission paths are captured (case 3) —
 * never a share (case 4).
 */
export function capturesShopperAnswers(params: {
  actionName: string;
  hasCustomerId: boolean;
}): boolean {
  const { actionName, hasCustomerId } = params;

  if (!hasCustomerId) {
    return true;
  }

  return SHOPPER_SUBMISSION_ACTIONS.indexOf(actionName) > -1;
}
